/**
 * Zokos First-Party Behavioral Telemetry & Google Ads Attribution Tracker (v2)
 *
 * - First-party, ad-block-resilient telemetry streaming via navigator.sendBeacon() to
 *   https://rscanconnection.rscan.com/api/zk-s (synced into Cloud SQL rscan-finance for Argon 4).
 * - Captures Google Ads auto-tagging (gclid/gbraid/wbraid/gad_campaignid), ValueTrack parameters
 *   (zk_cid, zk_agid, zk_kw, zk_mt, zk_dev, zk_net, zk_item, zk_tid), and UTM parameters into
 *   90-day first-party storage and Shopify Cart attributes (_zk_*).
 * - Tracks granular behavioral signals for Argon 4 conversion optimization:
 *   1. Landing URL, referrer, viewport (vw x vh), device/browser/OS, connection effectiveType.
 *   2. Time to first interaction (ttfiMs), active foreground dwell (engagedTimeS), total elapsed (totalTimeS).
 *   3. Max scroll depth % + per-element IntersectionObserver viewport dwell (ms) on price, save badge,
 *      above-the-fold condition box, buy buttons, shipping promises, full condition block, specs table, FAQ, terms.
 *   4. Every click target (tag, id, classes, compact selector, non-PII label, PDP zone, coordinates)
 *      with automatic Dead Click detection (non-interactive click with no state change) and Rage Click
 *      detection (3+ clicks within 1,000ms in a 40px radius).
 *   5. JS runtime errors (window.onerror & unhandledrejection).
 *   6. Full PDP -> Variant Switch -> Add to Cart -> View Cart -> Checkout funnel steps, from the in-page buttons and the
 *      phone sticky buy bar (zone sticky_buy_bar). Make an Offer is its own signal: the app's button copies the Add to
 *      cart button's classes, but a click on it never counts as add to cart (2026-10-22). Clicks a script makes (the
 *      buy bar handing a tap to the real button) are not logged. Since 2026-10-25 addedToCart means the add succeeded
 *      (the theme's product:added), not that the button was tapped, and the cart drawer's outcomes (open, closed by
 *      Back or a tap, left open) ride in the click path as ZK-EVENT rows.
 * - Preserves GA4 (G-GEQ6GDV5HW) and Microsoft Clarity (yzqoqbz1ka) custom tag enrichment.
 * - Strictly PII-free: never reads or transmits emails, names, addresses, phone numbers, or input values.
 */
(function () {
  'use strict';

  if (window.__zkStorefrontTelemetryV2) return;
  window.__zkStorefrontTelemetryV2 = true;

  var STORAGE_KEY = '_zk_ad_attr_v1';
  var SESSION_KEY = '_zk_ad_sess_v2';
  var COOKIE_VID = '_zk_vid';
  var GA4_MEASUREMENT_ID = 'G-GEQ6GDV5HW';
  var BEACON_ENDPOINT = 'https://rscanconnection.rscan.com/api/zk-s';
  var NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
  var SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  var SESSION_TTL_MS = 30 * 60 * 1000;
  var pageLoadedAt = nowMs();

  function nowMs() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function randomId(prefix) {
    var rand = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 6);
    return prefix + '_' + nowMs().toString(36) + '_' + rand;
  }

  function getCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function setCookie(name, value, maxAgeSeconds) {
    try {
      var secure = window.location && window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        name +
        '=' +
        encodeURIComponent(String(value || '')) +
        '; Path=/; SameSite=Lax; Max-Age=' +
        String(maxAgeSeconds || 7776000) +
        secure;
    } catch (e) {}
  }

  function readJsonStorage(storage, key) {
    try {
      var raw = storage && storage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeJsonStorage(storage, key, val) {
    try {
      if (storage) storage.setItem(key, JSON.stringify(val));
    } catch (e) {}
  }

  function isAnalyticsAllowed() {
    try {
      if (
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function'
      ) {
        return window.Shopify.customerPrivacy.analyticsProcessingAllowed() !== false;
      }
    } catch (e) {}
    return true;
  }

  // Strip any accidental email or phone pattern from element text labels
  function cleanTextLabel(raw, maxLen) {
    if (!raw) return '';
    var s = String(raw)
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    s = s.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[redacted]');
    return s.slice(0, maxLen || 80);
  }

  // Detect browser, OS, device category, and connection type without PII
  function detectEnvironment() {
    var ua = (navigator && navigator.userAgent) || '';
    var vw = window.innerWidth || (document.documentElement && document.documentElement.clientWidth) || 0;
    var vh = window.innerHeight || (document.documentElement && document.documentElement.clientHeight) || 0;
    var dpr = window.devicePixelRatio || 1;

    var deviceType = 'DESKTOP';
    if (/ipad|tablet|kindle|playbook|silk/i.test(ua) || (vw >= 600 && vw <= 1024 && /mobile|touch/i.test(ua))) {
      deviceType = 'TABLET';
    } else if (/mobile|iphone|ipod|android.*mobile|windows phone|blackberry/i.test(ua) || (vw > 0 && vw < 768)) {
      deviceType = 'MOBILE';
    }

    var browserName = 'Other';
    if (/edg\//i.test(ua)) browserName = 'Edge';
    else if (/opr\/|opera/i.test(ua)) browserName = 'Opera';
    else if (/crios|chrome\//i.test(ua)) browserName = 'Chrome';
    else if (/fxios|firefox\//i.test(ua)) browserName = 'Firefox';
    else if (/safari\//i.test(ua) && !/chrome|crios|android/i.test(ua)) browserName = 'Safari';

    var osName = 'Other';
    if (/iphone|ipad|ipod/i.test(ua)) osName = 'iOS';
    else if (/android/i.test(ua)) osName = 'Android';
    else if (/windows nt/i.test(ua)) osName = 'Windows';
    else if (/mac os x/i.test(ua)) osName = 'macOS';
    else if (/cros/i.test(ua)) osName = 'ChromeOS';
    else if (/linux/i.test(ua)) osName = 'Linux';

    var conn = (navigator && (navigator.connection || navigator.mozConnection || navigator.webkitConnection)) || null;
    var connectionType = (conn && conn.effectiveType) || null;

    return {
      viewportW: vw,
      viewportH: vh,
      dpr: dpr,
      deviceType: deviceType,
      browserName: browserName,
      osName: osName,
      connectionType: connectionType
    };
  }

  // Mask sensitive inputs for Microsoft Clarity privacy safety
  function applyPrivacyMasks() {
    try {
      var selectors = [
        'input[type="email"]',
        'input[type="tel"]',
        'input[type="password"]',
        'input[name*="email" i]',
        'input[name*="phone" i]',
        'input[name*="name" i]',
        'input[name*="address" i]',
        'textarea[name*="note" i]',
        '.cart-recap__note textarea'
      ];
      var nodes = document.querySelectorAll(selectors.join(','));
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].setAttribute('data-clarity-mask', 'true');
      }
    } catch (e) {}
  }

  // Parse current URL parameters
  function parseUrlAttribution() {
    var p;
    try {
      p = new URLSearchParams(window.location.search || '');
    } catch (e) {
      return {};
    }
    var gclid = p.get('gclid') || p.get('gbraid') || p.get('wbraid') || null;
    var zkCid = p.get('zk_cid') || p.get('gad_campaignid') || null;
    var zkAgid = p.get('zk_agid') || null;
    var zkKw = p.get('zk_kw') || null;
    var zkMt = p.get('zk_mt') || null;
    var zkDev = p.get('zk_dev') || null;
    var zkNet = p.get('zk_net') || null;
    var zkItem = p.get('zk_item') || null;
    var zkTid = p.get('zk_tid') || null;
    var utmSource = p.get('utm_source') || null;
    var utmMedium = p.get('utm_medium') || null;
    var utmCampaign = p.get('utm_campaign') || null;
    var utmTerm = p.get('utm_term') || null;
    var utmContent = p.get('utm_content') || null;
    var variant = p.get('variant') || null;

    var isAdLanding = Boolean(
      gclid ||
        zkCid ||
        (utmSource && utmSource.toLowerCase() === 'google' && utmMedium && utmMedium.toLowerCase() === 'cpc')
    );

    return {
      isAdLanding: isAdLanding,
      gclid: gclid,
      zkCid: zkCid || utmCampaign,
      zkAgid: zkAgid || utmContent,
      zkKw: zkKw || utmTerm,
      zkMt: zkMt,
      zkDev: zkDev,
      zkNet: zkNet,
      zkItem: zkItem,
      zkTid: zkTid,
      utmSource: utmSource,
      utmMedium: utmMedium,
      utmCampaign: utmCampaign,
      utmTerm: utmTerm,
      utmContent: utmContent,
      variant: variant
    };
  }

  var now = nowMs();
  var envInfo = detectEnvironment();
  var storedAttr = readJsonStorage(window.localStorage, STORAGE_KEY);
  if (storedAttr && storedAttr.updatedAt && now - storedAttr.updatedAt > NINETY_DAYS_MS) {
    storedAttr = null;
  }

  var vid = (storedAttr && storedAttr.vid) || getCookie(COOKIE_VID) || randomId('zkv');
  setCookie(COOKIE_VID, vid, 90 * 24 * 60 * 60);

  var sess = readJsonStorage(window.sessionStorage, SESSION_KEY);
  var isNewSession = !sess || !sess.sid || !sess.lastActiveAt || now - sess.lastActiveAt > SESSION_TTL_MS;

  var urlAttr = parseUrlAttribution();
  var isReturn7d = false;
  if (
    storedAttr &&
    storedAttr.lastSessionStartAt &&
    isNewSession &&
    now - storedAttr.lastSessionStartAt > 60 * 1000 &&
    now - storedAttr.lastSessionStartAt <= SEVEN_DAYS_MS
  ) {
    isReturn7d = true;
  }

  if (!storedAttr) {
    storedAttr = {
      vid: vid,
      firstTouchAt: now,
      updatedAt: now,
      lastSessionStartAt: now,
      sessionCount: 1,
      firstGclid: urlAttr.gclid || null,
      gclid: urlAttr.gclid || null,
      zkCid: urlAttr.zkCid || null,
      zkAgid: urlAttr.zkAgid || null,
      zkKw: urlAttr.zkKw || null,
      zkMt: urlAttr.zkMt || null,
      zkDev: urlAttr.zkDev || null,
      zkNet: urlAttr.zkNet || null,
      zkItem: urlAttr.zkItem || null,
      zkTid: urlAttr.zkTid || null,
      utmSource: urlAttr.utmSource || null,
      utmMedium: urlAttr.utmMedium || null,
      utmCampaign: urlAttr.utmCampaign || null,
      utmTerm: urlAttr.utmTerm || null,
      utmContent: urlAttr.utmContent || null,
      landingPath: window.location.pathname || '/',
      landingQuery: (window.location.search || '').replace(/^\?/, '')
    };
  } else {
    storedAttr.vid = vid;
    storedAttr.updatedAt = now;
    if (isNewSession) {
      storedAttr.sessionCount = (storedAttr.sessionCount || 1) + 1;
      storedAttr.lastSessionStartAt = now;
    }
    if (urlAttr.isAdLanding) {
      if (urlAttr.gclid) {
        if (!storedAttr.firstGclid) storedAttr.firstGclid = urlAttr.gclid;
        storedAttr.gclid = urlAttr.gclid;
      }
      if (urlAttr.zkCid) storedAttr.zkCid = urlAttr.zkCid;
      if (urlAttr.zkAgid) storedAttr.zkAgid = urlAttr.zkAgid;
      if (urlAttr.zkKw) storedAttr.zkKw = urlAttr.zkKw;
      if (urlAttr.zkMt) storedAttr.zkMt = urlAttr.zkMt;
      if (urlAttr.zkDev) storedAttr.zkDev = urlAttr.zkDev;
      if (urlAttr.zkNet) storedAttr.zkNet = urlAttr.zkNet;
      if (urlAttr.zkItem) storedAttr.zkItem = urlAttr.zkItem;
      if (urlAttr.zkTid) storedAttr.zkTid = urlAttr.zkTid;
      if (urlAttr.utmSource) storedAttr.utmSource = urlAttr.utmSource;
      if (urlAttr.utmMedium) storedAttr.utmMedium = urlAttr.utmMedium;
      if (urlAttr.utmCampaign) storedAttr.utmCampaign = urlAttr.utmCampaign;
      storedAttr.landingPath = window.location.pathname || '/';
      storedAttr.landingQuery = (window.location.search || '').replace(/^\?/, '');
    }
  }
  writeJsonStorage(window.localStorage, STORAGE_KEY, storedAttr);

  if (isNewSession) {
    sess = {
      sid: randomId('zks'),
      startedAt: now,
      lastActiveAt: now,
      sessionDate: new Date(now).toISOString().slice(0, 10),
      landingPath: window.location.pathname || '/',
      landingQuery: (window.location.search || '').replace(/^\?/, ''),
      referrer: document.referrer ? String(document.referrer).slice(0, 250) : null,
      pageViews: 1,
      ttfiMs: null,
      engagedTimeS: 0,
      totalTimeS: 0,
      maxScrollPct: 0,
      galleryViews: 0,
      galleryZoomed: false,
      specsViewed: false,
      pdfClicked: false,
      faqOpened: false,
      trustClicked: false,
      variantChanged: false,
      skuCopied: false,
      makeOfferClicked: false,
      internalPivot: false,
      addedToCart: false,
      viewedCart: Boolean(window.theme && window.theme.pageType === 'cart'),
      beganCheckout: false,
      quickbackClick: false,
      isReturn7d: isReturn7d,
      deadClicks: 0,
      rageClicks: 0,
      jsErrors: 0,
      elementDwellMs: {},
      clicks: [],
      errors: [],
      variantSwitches: [],
      milestones: [],
      landingFired: false,
      emittedMilestones: {},
      lastCartSyncScore: -1
    };
  } else {
    sess.lastActiveAt = now;
    sess.pageViews = (sess.pageViews || 1) + 1;
    if (sess.pageViews >= 2 && (window.location.pathname || '').indexOf('/products/') === 0) {
      sess.internalPivot = true;
    }
    if (window.theme && window.theme.pageType === 'cart') {
      sess.viewedCart = true;
    }
    if (isReturn7d) sess.isReturn7d = true;
    sess.elementDwellMs = sess.elementDwellMs || {};
    sess.clicks = sess.clicks || [];
    sess.errors = sess.errors || [];
    sess.variantSwitches = sess.variantSwitches || [];
    sess.milestones = sess.milestones || [];
    sess.emittedMilestones = sess.emittedMilestones || {};
  }
  writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);

  var hasAdAttribution = Boolean(storedAttr.gclid || storedAttr.zkCid || urlAttr.isAdLanding);
  // A count, not a time: a change made in the same handler as the tap often falls in the same millisecond, and a
  // timestamp comparison then logged that tap as dead (2026-10-23).
  var stateMutations = 0;

  function markStateMutation() {
    stateMutations += 1;
  }

  // Extract Product Context on product pages (PII-free)
  function getProductContext() {
    var ctx = window.zokosTrackingProduct || {};
    var selectedCondition = ctx.condition || null;
    try {
      var condEl = document.querySelector('[data-zokos-condition] .zokos-condition__item.is-selected');
      if (condEl) {
        selectedCondition = condEl.getAttribute('data-zokos-grade-value') || selectedCondition;
      }
    } catch (e) {}

    var handle = ctx.handle || null;
    if (!handle && (window.location.pathname || '').indexOf('/products/') === 0) {
      var m = window.location.pathname.match(/^\/products\/([^/?#]+)/i);
      if (m) handle = m[1];
    }

    var priceCents = Number(ctx.priceCents) || 0;
    var compareCents = Number(ctx.compareAtPriceCents) || 0;
    var savingsPct =
      compareCents > priceCents && compareCents > 0
        ? Math.round(((compareCents - priceCents) / compareCents) * 100)
        : 0;
    var freeShip = priceCents >= 2500;
    var priceBand =
      priceCents <= 0
        ? 'unknown'
        : priceCents < 2500
        ? 'under_25'
        : priceCents < 7500
        ? '25_to_75'
        : priceCents < 20000
        ? '75_to_200'
        : '200_plus';

    return {
      productId: ctx.productId ? String(ctx.productId) : null,
      handle: handle,
      vendor: ctx.vendor || null,
      variantId: ctx.variantId ? String(ctx.variantId) : urlAttr.variant || null,
      sku: ctx.sku || null,
      condition: selectedCondition,
      priceCents: priceCents,
      compareCents: compareCents,
      savingsPct: savingsPct,
      freeShip: freeShip,
      priceBand: priceBand
    };
  }

  // Deterministic Intent Score v1 (matches zokos-growth-engine/src/adTracking/intentScore.js)
  function computeIntentScore() {
    var signals = [];
    var dwellPts = 0;
    if (sess.engagedTimeS >= 60) {
      dwellPts = 20;
      signals.push('time60s');
    } else if (sess.engagedTimeS >= 30) {
      dwellPts = 14;
      signals.push('time30s');
    } else if (sess.engagedTimeS >= 10) {
      dwellPts = 8;
      signals.push('time10s');
    }

    var scrollPts = 0;
    if (sess.maxScrollPct >= 90) {
      scrollPts = 15;
      signals.push('scroll90');
    } else if (sess.maxScrollPct >= 75 || sess.specsViewed) {
      scrollPts = 12;
      signals.push(sess.maxScrollPct >= 75 ? 'scroll75' : 'specs_view');
    } else if (sess.maxScrollPct >= 50) {
      scrollPts = 7;
      signals.push('scroll50');
    } else if (sess.maxScrollPct >= 25) {
      scrollPts = 3;
      signals.push('scroll25');
    }
    if (sess.specsViewed && signals.indexOf('specs_view') === -1) {
      signals.push('specs_view');
    }

    var evalRaw = 0;
    if (sess.galleryViews >= 1) {
      evalRaw += 6;
      signals.push(sess.galleryViews >= 3 ? 'gal3+' : 'gal1');
    }
    if (sess.galleryViews >= 3 || sess.galleryZoomed) {
      evalRaw += 6;
      if (sess.galleryZoomed) signals.push('zoom');
    }
    if (sess.pdfClicked) {
      evalRaw += 12;
      signals.push('pdf');
    }
    if (sess.faqOpened || sess.trustClicked) {
      evalRaw += 6;
      if (sess.faqOpened) signals.push('faq');
      if (sess.trustClicked) signals.push('trust');
    }
    if (sess.variantChanged) {
      evalRaw += 8;
      signals.push('variant');
    }
    if (sess.skuCopied) {
      evalRaw += 8;
      signals.push('copy_sku');
    }
    var evaluationPts = Math.min(25, evalRaw);

    var multiRaw = 0;
    if (sess.internalPivot) {
      multiRaw += 6;
      signals.push('pivot');
    }
    if (sess.isReturn7d) {
      multiRaw += 12;
      signals.push('return7d');
    }
    var multiVisitPts = Math.min(15, multiRaw);

    var commercialPts = 0;
    if (sess.beganCheckout) {
      commercialPts = 50;
      signals.push('checkout');
    } else if (sess.viewedCart) {
      commercialPts = 40;
      signals.push('view_cart');
    } else if (sess.addedToCart) {
      commercialPts = 35;
      signals.push('atc');
    } else if (sess.makeOfferClicked) {
      commercialPts = 30;
      signals.push('make_offer');
    }

    if (sess.deadClicks > 0) signals.push('dead_click');
    if (sess.rageClicks > 0) signals.push('rage_click');

    var anyInteraction =
      evalRaw > 0 || multiRaw > 0 || commercialPts > 0 || sess.maxScrollPct >= 25 || sess.specsViewed;
    var isFastBounce10s = sess.engagedTimeS < 10 && !anyInteraction;
    if (isFastBounce10s) signals.push('fast_bounce');

    var rawTotal = dwellPts + scrollPts + evaluationPts + multiVisitPts + commercialPts;
    var score = isFastBounce10s ? Math.min(5, rawTotal) : Math.min(100, rawTotal);

    var tier = 'bounce';
    if (sess.beganCheckout || score >= 85) tier = 'buyer';
    else if (score >= 60) tier = 'high';
    else if (score >= 30) tier = 'medium';
    else if (score >= 10) tier = 'low';

    return {
      score: score,
      tier: tier,
      isFastBounce10s: isFastBounce10s,
      signals: signals,
      signalsCsv: signals.join(',')
    };
  }

  // Build full telemetry payload for First-Party Beacon -> rscan-finance DB
  function buildBeaconPayload(reason) {
    var scored = computeIntentScore();
    var prod = getProductContext();
    var elapsedS = Math.max(sess.engagedTimeS || 0, Math.round((nowMs() - (sess.startedAt || pageLoadedAt)) / 1000));
    sess.totalTimeS = Math.max(sess.totalTimeS || 0, elapsedS);

    return {
      reason: reason || 'periodic',
      sid: sess.sid,
      vid: storedAttr.vid,
      gclid: storedAttr.gclid,
      firstGclid: storedAttr.firstGclid || storedAttr.gclid,
      zkCid: storedAttr.zkCid,
      zkAgid: storedAttr.zkAgid,
      zkKw: storedAttr.zkKw,
      zkMt: storedAttr.zkMt,
      zkDev: storedAttr.zkDev || envInfo.deviceType,
      zkNet: storedAttr.zkNet,
      zkItem: storedAttr.zkItem,
      zkTid: storedAttr.zkTid,
      utmSource: storedAttr.utmSource,
      utmMedium: storedAttr.utmMedium,
      utmCampaign: storedAttr.utmCampaign,
      utmTerm: storedAttr.utmTerm,
      utmContent: storedAttr.utmContent,
      referrer: sess.referrer,
      sessionDate: sess.sessionDate,
      startedAt: sess.startedAt,
      landingPath: sess.landingPath || storedAttr.landingPath || '/',
      landingQuery: sess.landingQuery || storedAttr.landingQuery || '',
      pagePath: window.location.pathname || '/',
      productHandle: prod.handle,
      productId: prod.productId,
      variantId: prod.variantId,
      sku: prod.sku,
      brand: prod.vendor,
      conditionGrade: prod.condition,
      priceCents: prod.priceCents,
      compareCents: prod.compareCents,
      savingsPct: prod.savingsPct,
      freeShip: prod.freeShip,
      viewportW: envInfo.viewportW,
      viewportH: envInfo.viewportH,
      dpr: envInfo.dpr,
      deviceType: envInfo.deviceType,
      browserName: envInfo.browserName,
      osName: envInfo.osName,
      connectionType: envInfo.connectionType,
      pageViews: sess.pageViews,
      ttfiMs: sess.ttfiMs,
      engagedTimeS: sess.engagedTimeS,
      totalTimeS: sess.totalTimeS,
      maxScrollPct: sess.maxScrollPct,
      galleryViews: sess.galleryViews,
      galleryZoomed: sess.galleryZoomed,
      specsViewed: sess.specsViewed,
      pdfClicked: sess.pdfClicked,
      faqOpened: sess.faqOpened,
      trustClicked: sess.trustClicked,
      variantChanged: sess.variantChanged,
      skuCopied: sess.skuCopied,
      makeOfferClicked: sess.makeOfferClicked,
      internalPivot: sess.internalPivot,
      addedToCart: sess.addedToCart,
      viewedCart: sess.viewedCart,
      beganCheckout: sess.beganCheckout,
      quickbackClick: sess.quickbackClick,
      isReturn7d: sess.isReturn7d,
      deadClicks: sess.deadClicks || 0,
      rageClicks: sess.rageClicks || 0,
      jsErrors: sess.jsErrors || 0,
      intentScore: scored.score,
      intentTier: scored.tier,
      signalsCsv: scored.signalsCsv,
      elementDwellMs: sess.elementDwellMs || {},
      clicks: (sess.clicks || []).slice(-50),
      errors: (sess.errors || []).slice(-20),
      variantSwitches: (sess.variantSwitches || []).slice(-20),
      milestones: (sess.milestones || []).slice(-35)
    };
  }

  function sendFirstPartyBeacon(reason) {
    if (!isAnalyticsAllowed()) return;
    try {
      flushActiveElementDwells();
      var bodyStr = JSON.stringify(buildBeaconPayload(reason));
      var sent = false;
      if (navigator && typeof navigator.sendBeacon === 'function') {
        try {
          var blob = new Blob([bodyStr], { type: 'text/plain;charset=UTF-8' });
          sent = navigator.sendBeacon(BEACON_ENDPOINT, blob);
        } catch (e) {
          sent = navigator.sendBeacon(BEACON_ENDPOINT, bodyStr);
        }
      }
      if (!sent && window.fetch) {
        fetch(BEACON_ENDPOINT, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: bodyStr,
          keepalive: true
        }).catch(function () {});
      }
    } catch (e) {}
  }

  // Initialize main-window GA4 custom event bridge with send_page_view: false
  var gtagReady = false;
  function ensureMainWindowGtag() {
    if (gtagReady || !isAnalyticsAllowed()) return;
    gtagReady = true;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', GA4_MEASUREMENT_ID, {
        send_page_view: false,
        transport_type: 'beacon'
      });
      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA4_MEASUREMENT_ID;
      var firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(s, firstScript);
      } else if (document.head) {
        document.head.appendChild(s);
      }
    }
  }

  function sendGa4CustomEvent(eventName, extraParams) {
    if (!hasAdAttribution || !isAnalyticsAllowed()) return;
    ensureMainWindowGtag();
    if (typeof window.gtag !== 'function') return;

    var scored = computeIntentScore();
    var prod = getProductContext();
    var payload = {
      send_to: GA4_MEASUREMENT_ID,
      transport_type: 'beacon',
      zk_gclid: storedAttr.gclid || '(not set)',
      zk_vid: storedAttr.vid || '(not set)',
      zk_sid: sess.sid || '(not set)',
      zk_kw: storedAttr.zkKw || '(not set)',
      zk_mt: storedAttr.zkMt || '(not set)',
      zk_agid: storedAttr.zkAgid || '(not set)',
      zk_condition: prod.condition || '(not set)',
      zk_intent_tier: scored.tier,
      zk_signals: scored.signalsCsv.slice(0, 100),
      zk_intent_score: scored.score,
      zk_engaged_s: sess.engagedTimeS,
      zk_scroll_pct: sess.maxScrollPct
    };
    if (extraParams) {
      for (var k in extraParams) {
        if (Object.prototype.hasOwnProperty.call(extraParams, k)) {
          payload[k] = extraParams[k];
        }
      }
    }
    try {
      window.gtag('event', eventName, payload);
    } catch (e) {}
  }

  // Enrich Microsoft Clarity (yzqoqbz1ka) with custom session tags and milestone events
  function syncClarityTags(milestoneEvent) {
    if (!isAnalyticsAllowed()) return;
    try {
      if (typeof window.clarity !== 'function') return;
      var scored = computeIntentScore();
      var prod = getProductContext();

      window.clarity('identify', storedAttr.vid, sess.sid, window.location.pathname || '/');
      if (storedAttr.gclid) window.clarity('set', 'zk_gclid', storedAttr.gclid);
      if (storedAttr.zkCid) window.clarity('set', 'zk_campaign_id', String(storedAttr.zkCid));
      if (storedAttr.zkAgid) window.clarity('set', 'zk_adgroup_id', String(storedAttr.zkAgid));
      if (storedAttr.zkKw) window.clarity('set', 'zk_keyword', String(storedAttr.zkKw));
      if (storedAttr.zkMt) window.clarity('set', 'zk_matchtype', String(storedAttr.zkMt));
      if (storedAttr.zkDev) window.clarity('set', 'zk_device', String(storedAttr.zkDev));
      if (storedAttr.zkItem) window.clarity('set', 'zk_item_id', String(storedAttr.zkItem));

      if (prod.condition) window.clarity('set', 'zk_condition', String(prod.condition));
      if (prod.vendor) window.clarity('set', 'zk_brand', String(prod.vendor));
      if (prod.sku) window.clarity('set', 'zk_sku', String(prod.sku));
      if (prod.priceBand) window.clarity('set', 'zk_price_band', String(prod.priceBand));
      window.clarity('set', 'zk_free_ship', prod.freeShip ? 'yes' : 'no');

      window.clarity('set', 'zk_intent_score', String(scored.score));
      window.clarity('set', 'zk_intent_tier', String(scored.tier));
      window.clarity('set', 'zk_signals', String(scored.signalsCsv));
      window.clarity('set', 'zk_fast_bounce', scored.isFastBounce10s ? 'true' : 'false');

      if (milestoneEvent) {
        window.clarity('event', milestoneEvent);
      }
    } catch (e) {}
  }

  // Sync attribution & intent score to Shopify Cart attributes (_zk_*)
  var cartSyncTimer = null;
  function syncCartAttributes(immediate) {
    if (!hasAdAttribution || !window.fetch) return;
    var run = function () {
      var scored = computeIntentScore();
      if (!immediate && sess.lastCartSyncScore === scored.score) return;
      sess.lastCartSyncScore = scored.score;
      writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);

      var attrs = {
        _zk_vid: storedAttr.vid || '',
        _zk_sid: sess.sid || '',
        _zk_gclid: storedAttr.gclid || '',
        _zk_first_gclid: storedAttr.firstGclid || storedAttr.gclid || '',
        _zk_cid: storedAttr.zkCid || '',
        _zk_agid: storedAttr.zkAgid || '',
        _zk_kw: storedAttr.zkKw || '',
        _zk_mt: storedAttr.zkMt || '',
        _zk_dev: storedAttr.zkDev || '',
        _zk_item: storedAttr.zkItem || '',
        _zk_intent_score: String(scored.score),
        _zk_intent_tier: scored.tier,
        _zk_signals: scored.signalsCsv.slice(0, 120)
      };

      var root = (window.routes && window.routes.rootUrlWithoutSlash) || '';
      fetch(root + '/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes: attrs }),
        keepalive: true
      }).catch(function () {});
    };

    if (immediate) {
      if (cartSyncTimer) clearTimeout(cartSyncTimer);
      run();
    } else {
      if (cartSyncTimer) clearTimeout(cartSyncTimer);
      cartSyncTimer = setTimeout(run, 2500);
    }
  }

  function recordMilestone(key, clarityEventName, flushImmediately) {
    var t = nowMs();
    sess.lastActiveAt = t;
    if (key) {
      sess.milestones = sess.milestones || [];
      if (!sess.emittedMilestones[key]) {
        sess.emittedMilestones[key] = 1;
        sess.milestones.push({ name: key, ts: t });
        sendGa4CustomEvent('zk_signal', { zk_milestone: key });
      }
    }
    writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
    syncClarityTags(clarityEventName || null);
    syncCartAttributes(false);
    if (flushImmediately) {
      sendFirstPartyBeacon('milestone:' + (key || 'event'));
    }
  }

  // A cart event that is not a tap (2026-10-25): it rides in the click path, which the growth engine stores as sent
  // (click_path_json), as tag ZK-EVENT in zone cart_drawer. Readouts leave ZK-EVENT rows out of tap counts.
  function logCartEvent(name) {
    sess.clicks = sess.clicks || [];
    if (sess.clicks.length >= 60) return;
    var t = nowMs();
    sess.clicks.push({
      id: 'e_' + t.toString(36) + '_' + (sess.clicks.length + 1),
      ts: t,
      tag: 'ZK-EVENT',
      elId: null,
      cls: null,
      sel: null,
      txt: name,
      href: null,
      zone: 'cart_drawer',
      dead: false,
      rage: false,
      xPct: 0,
      yPx: 0,
      path: window.location.pathname || '/'
    });
    writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
  }

  // Fire initial landing beacon (captures 100% of arrivals even if they bounce in <1.5s)
  if (!sess.landingFired) {
    sess.landingFired = true;
    writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
    if (hasAdAttribution) {
      sendGa4CustomEvent('zk_landing');
      syncCartAttributes(true);
    }
  }
  setTimeout(function () {
    sendFirstPartyBeacon('landing');
  }, 300);

  if (typeof window.clarity === 'function') {
    syncClarityTags(urlAttr.isAdLanding ? 'zk_ad_landing' : null);
  } else {
    var clarityPollCount = 0;
    var clarityPoll = setInterval(function () {
      clarityPollCount++;
      if (typeof window.clarity === 'function') {
        syncClarityTags(urlAttr.isAdLanding ? 'zk_ad_landing' : null);
        clearInterval(clarityPoll);
      } else if (clarityPollCount >= 15) {
        clearInterval(clarityPoll);
      }
    }, 1000);
  }

  // 1. Time to First Interaction (ttfiMs) & Active Foreground Time Tracker
  var lastUserActivityAt = nowMs();
  function markUserActivity() {
    var t = nowMs();
    lastUserActivityAt = t;
    if (sess.ttfiMs === null || sess.ttfiMs === undefined) {
      sess.ttfiMs = Math.max(1, t - pageLoadedAt);
      writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
    }
  }
  window.addEventListener('scroll', markUserActivity, { passive: true });
  window.addEventListener('pointerdown', markUserActivity, { passive: true });
  window.addEventListener('keydown', markUserActivity, { passive: true });
  window.addEventListener('touchstart', markUserActivity, { passive: true });

  setInterval(function () {
    if (document.visibilityState === 'hidden') return;
    var t = nowMs();
    sess.totalTimeS = Math.max(sess.totalTimeS || 0, Math.round((t - (sess.startedAt || pageLoadedAt)) / 1000));
    if (t - lastUserActivityAt > 45000) return;

    sess.engagedTimeS = (sess.engagedTimeS || 0) + 1;
    sess.lastActiveAt = t;

    if (sess.engagedTimeS === 10) recordMilestone('time10s', null, true);
    else if (sess.engagedTimeS === 30) recordMilestone('time30s', null, true);
    else if (sess.engagedTimeS === 60) recordMilestone('time60s', 'zk_engaged_60s', true);
    else if (sess.engagedTimeS % 15 === 0) {
      writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
      sendFirstPartyBeacon('heartbeat');
    }
  }, 1000);

  // 2. Scroll Depth Tracker (25 / 50 / 75 / 90%)
  var scrollTicking = false;
  function checkScrollDepth() {
    scrollTicking = false;
    try {
      var docEl = document.documentElement;
      var body = document.body;
      var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop || 0;
      var viewportH = window.innerHeight || docEl.clientHeight || 0;
      var fullH = Math.max(
        body.scrollHeight || 0,
        docEl.scrollHeight || 0,
        body.offsetHeight || 0,
        docEl.offsetHeight || 0
      );
      if (fullH <= viewportH + 80) return;
      var pct = Math.min(100, Math.round(((scrollTop + viewportH) / fullH) * 100));
      if (pct > (sess.maxScrollPct || 0)) {
        var prev = sess.maxScrollPct || 0;
        sess.maxScrollPct = pct;
        if (prev < 25 && pct >= 25) recordMilestone('scroll25');
        if (prev < 50 && pct >= 50) recordMilestone('scroll50');
        if (prev < 75 && pct >= 75) recordMilestone('scroll75');
        if (prev < 90 && pct >= 90) recordMilestone('scroll90');
      }
    } catch (e) {}
  }

  window.addEventListener(
    'scroll',
    function () {
      if (!scrollTicking) {
        scrollTicking = true;
        if (window.requestAnimationFrame) window.requestAnimationFrame(checkScrollDepth);
        else setTimeout(checkScrollDepth, 150);
      }
    },
    { passive: true }
  );

  // 3. Per-Element Viewport Dwell Tracker (IntersectionObserver across key PDP conversion elements)
  var activeVisibleStart = {};
  function flushActiveElementDwells() {
    var t = nowMs();
    for (var k in activeVisibleStart) {
      if (Object.prototype.hasOwnProperty.call(activeVisibleStart, k) && activeVisibleStart[k]) {
        var delta = Math.max(0, t - activeVisibleStart[k]);
        sess.elementDwellMs[k] = (sess.elementDwellMs[k] || 0) + delta;
        activeVisibleStart[k] = document.visibilityState === 'hidden' ? 0 : t;
        if (k === 'specs_table' && sess.elementDwellMs[k] >= 1500 && !sess.specsViewed) {
          sess.specsViewed = true;
          recordMilestone('specs_view', 'zk_specs_viewed', true);
        }
        if (k === 'faq_section' && sess.elementDwellMs[k] >= 1500 && !sess.faqOpened) {
          sess.faqOpened = true;
          recordMilestone('faq');
        }
      }
    }
  }

  function initVisibilityObservers() {
    if (typeof window.IntersectionObserver !== 'function') return;
    var targets = [
      { key: 'price_box', sel: '.product-form__info-item .price-list, .product-meta__price-list-container, .zokos-mobile-atf-price' },
      { key: 'save_badge', sel: '.zokos-price-save-badge, .price--compare, .product-label--on-sale' },
      { key: 'atf_condition_box', sel: '.zokos-atf-trust, [data-zokos-condition-lead]' },
      { key: 'buy_buttons', sel: '.product-form__payment-container, [data-action="add-to-cart"]' },
      { key: 'shipping_promises', sel: '.zokos-promises' },
      { key: 'condition_details', sel: '.zokos-condition, [data-zokos-condition]' },
      {
        key: 'specs_table',
        sel: '.zokos-content__specs, .product-block-list__item--description table, .product-block-list__item--description ul'
      },
      { key: 'faq_section', sel: '.zokos-content__faq' },
      { key: 'terms_disclaimer', sel: '.zokos-terms, [data-rscan-listing-disclaimer]' },
      { key: 'similar_products', sel: '[data-zokos-similar], .product-recommendations' },
      { key: 'trust_note', sel: '.zk-trustline' },
      { key: 'sticky_buy_bar', sel: '.zk-buybar' },
      { key: 'condition_sheet', sel: '[data-zk-sheet]' }
    ];

    var obs = new IntersectionObserver(
      function (entries) {
        var t = nowMs();
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          var key = entry.target.getAttribute('data-zk-observe');
          if (!key) continue;
          if (entry.isIntersecting && document.visibilityState !== 'hidden') {
            if (!activeVisibleStart[key]) {
              activeVisibleStart[key] = t;
            }
          } else if (activeVisibleStart[key]) {
            var delta = Math.max(0, t - activeVisibleStart[key]);
            activeVisibleStart[key] = 0;
            sess.elementDwellMs[key] = (sess.elementDwellMs[key] || 0) + delta;
            if (key === 'specs_table' && sess.elementDwellMs[key] >= 1500 && !sess.specsViewed) {
              sess.specsViewed = true;
              recordMilestone('specs_view', 'zk_specs_viewed', true);
            }
            if (key === 'faq_section' && sess.elementDwellMs[key] >= 1500 && !sess.faqOpened) {
              sess.faqOpened = true;
              recordMilestone('faq');
            }
          }
        }
      },
      { threshold: 0.35 }
    );

    for (var j = 0; j < targets.length; j++) {
      var el = document.querySelector(targets[j].sel);
      if (el) {
        el.setAttribute('data-zk-observe', targets[j].key);
        obs.observe(el);
      }
    }
  }

  // Helper to build compact CSS selector and semantic PDP zone for any clicked element
  function describeClickedElement(target) {
    var tag = (target && target.tagName ? target.tagName : 'UNKNOWN').toUpperCase();
    var elId = (target && target.id) || '';
    var cls = '';
    if (target && typeof target.className === 'string') {
      cls = target.className.trim().split(/\s+/).slice(0, 3).join('.');
    }
    var parentDesc = '';
    var p = target && target.parentElement;
    if (p && p.tagName) {
      var pTag = p.tagName.toLowerCase();
      var pCls = typeof p.className === 'string' && p.className.trim() ? '.' + p.className.trim().split(/\s+/)[0] : '';
      parentDesc = pTag + pCls + ' > ';
    }
    var selfDesc = tag.toLowerCase() + (elId ? '#' + elId : '') + (cls ? '.' + cls : '');
    var selector = (parentDesc + selfDesc).slice(0, 140);

    var zone = 'other';
    if (target && typeof target.closest === 'function') {
      if (target.closest('.zk-buybar')) zone = 'sticky_buy_bar';
      else if (target.closest('[data-zk-sheet]')) zone = 'condition_sheet';
      else if (target.closest('.zokos-savings')) zone = 'save_badge';
      else if (target.closest('.product-gallery, .product-block-list__item--gallery')) zone = 'gallery';
      else if (target.closest('.zokos-atf-trust, [data-zokos-condition-lead]')) zone = 'atf_trust';
      else if (target.closest('.zk-trustline')) zone = 'trust_note';
      else if (target.closest('.price-list, .product-meta__price-list-container, .zokos-price-save-badge, .zokos-mobile-atf-price')) zone = 'price_box';
      else if (target.closest('.product-form__variants, .block-swatch-list, .variant-swatch-list')) zone = 'variant_picker';
      else if (target.closest('.product-form__payment-container, .zokos-quick-links, [data-action="add-to-cart"]')) zone = 'buy_buttons';
      else if (target.closest('.zokos-promises')) zone = 'shipping_promises';
      else if (target.closest('.zokos-condition, [data-zokos-condition]')) zone = 'condition_details';
      else if (target.closest('.zokos-content__specs, table')) zone = 'specs_table';
      else if (target.closest('.zokos-content__faq, details')) zone = 'faq';
      else if (target.closest('[data-zokos-similar], .product-recommendations')) zone = 'similar_products';
      else if (target.closest('.mini-cart, .cart-recap')) zone = 'mini_cart';
      else if (target.closest('.header, .nav-bar, .search-bar')) zone = 'header_nav';
      else if (target.closest('.footer')) zone = 'footer';
    }

    // Extract safe, non-PII text label (never read input values)
    var txt = '';
    if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
      var interactiveEl =
        (target.closest && target.closest('button, a, summary, label, .zokos-condition__item, .block-swatch')) || target;
      txt = cleanTextLabel(
        interactiveEl.getAttribute('aria-label') || interactiveEl.textContent || target.getAttribute('alt') || '',
        80
      );
    }

    var linkEl = target.closest && target.closest('a[href]');
    var href = linkEl ? String(linkEl.getAttribute('href') || '').slice(0, 160) : null;

    return {
      tag: tag,
      elId: elId ? elId.slice(0, 60) : null,
      cls: cls ? cls.slice(0, 120) : null,
      sel: selector,
      txt: txt || null,
      href: href,
      zone: zone
    };
  }

  function isInteractiveTarget(target) {
    if (!target || typeof target.closest !== 'function') return false;
    var match = target.closest(
      'a[href], button:not([disabled]), input, select, textarea, summary, label, [role="button"], [role="tab"], [data-action], .product-gallery__thumbnail, .product-gallery__carousel-item, .flickity-button, .flickity-page-dots .dot, .block-swatch__item, .variant-swatch__item, .pswp__button, shopify-chat'
    );
    if (!match) return false;
    if (match.tagName === 'A') {
      var h = String(match.getAttribute('href') || '').trim();
      if (h === '#' || h === 'javascript:void(0)') {
        return Boolean(match.getAttribute('data-action') || match.onclick);
      }
    }
    return true;
  }

  // The Make an Offer app's button (#mf_container > button.mf_button) copies the Add to cart button's classes
  // (product-form__add-button), so it is matched first and never counts as add to cart; the phone buy bar has its own.
  // Express checkout (Shop Pay, Apple Pay, Google Pay, PayPal, ...): Shopify draws each button as a custom element in
  // <shopify-accelerated-checkout> (product form) or <shopify-accelerated-checkout-cart> (cart drawer, cart page), and a
  // tap is reported on that element. PayPal's button is an iframe, which the window blur listener below catches.
  var EXPRESS_CHECKOUT_SELECTOR =
    'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, shop-pay-wallet-button, shopify-apple-pay-button, ' +
    'shopify-google-pay-button, shopify-paypal-button, shopify-amazon-pay-button, shopify-venmo-button, ' +
    'more-payment-options-link, #more-payment-options-link, .shopify-payment-button__button, .shopify-payment-button__more-options';

  function beginCheckout() {
    sess.beganCheckout = true;
    markStateMutation();
    syncCartAttributes(true);
    recordMilestone('checkout', 'zk_begin_checkout', true);
  }

  function findMakeOfferButton(target) {
    var known = target.closest(
      '#mf_container, .mf_button, #make-an-offer-btn, [data-make-an-offer], .mao-button, [data-zk-buybar-offer]'
    );
    if (known) return known;
    var btn = target.closest('button, a');
    return btn && /make\s+an\s+offer/i.test(btn.textContent || btn.value || '') ? btn : null;
  }

  // 4. Delegated Click, Dead Click & Rage Click Listener
  var recentClickCoords = [];
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      // A click a script makes (the buy bar handing a tap to the real button, the zoom caption opening the photo) is
      // not the shopper's: keep it out of the click log and the funnel flags, but it still changes the page, so the
      // shopper's own tap that caused it is not a dead click.
      if (event.isTrusted === false) {
        markStateMutation();
        return;
      }

      var t = nowMs();
      var cx = Number(event.clientX) || 0;
      var cy = Number(event.clientY) || 0;
      var vw = window.innerWidth || 1;
      var scrollTop = window.pageYOffset || (document.documentElement && document.documentElement.scrollTop) || 0;
      var xPct = Math.max(0, Math.min(100, Math.round((cx / vw) * 100)));
      var yPx = Math.max(0, Math.round(cy + scrollTop));

      // Rage click check: 3+ clicks within 1000ms within 40px radius
      recentClickCoords.push({ ts: t, x: cx, y: cy });
      while (recentClickCoords.length > 0 && t - recentClickCoords[0].ts > 1000) {
        recentClickCoords.shift();
      }
      var nearbyCount = 0;
      for (var r = 0; r < recentClickCoords.length; r++) {
        var dx = recentClickCoords[r].x - cx;
        var dy = recentClickCoords[r].y - cy;
        if (Math.sqrt(dx * dx + dy * dy) <= 40) nearbyCount++;
      }
      var isRage = nearbyCount >= 3;
      if (isRage) {
        sess.rageClicks = (sess.rageClicks || 0) + 1;
      }

      var desc = describeClickedElement(target);
      var interactive = isInteractiveTarget(target);
      var clickId = 'c_' + t.toString(36) + '_' + (sess.clicks ? sess.clicks.length + 1 : 1);
      var clickRecord = {
        id: clickId,
        ts: t,
        tag: desc.tag,
        elId: desc.elId,
        cls: desc.cls,
        sel: desc.sel,
        txt: desc.txt,
        href: desc.href,
        zone: desc.zone,
        dead: false,
        rage: isRage,
        xPct: xPct,
        yPx: yPx,
        path: window.location.pathname || '/'
      };
      sess.clicks = sess.clicks || [];
      if (sess.clicks.length < 60) {
        sess.clicks.push(clickRecord);
      }

      // Check for dead click if target was not a standard interactive control
      if (!interactive) {
        var mutBefore = stateMutations;
        var urlBefore = window.location.href;
        setTimeout(function () {
          if (stateMutations === mutBefore && window.location.href === urlBefore) {
            clickRecord.dead = true;
            sess.deadClicks = (sess.deadClicks || 0) + 1;
            recordMilestone('dead_click', 'zk_dead_click', true);
          }
        }, 350);
      } else {
        markStateMutation();
      }

      if (isRage) {
        recordMilestone('rage_click', 'zk_rage_click', true);
      }

      // Gallery thumbnail / carousel navigation
      var thumb = target.closest(
        '.product-gallery__thumbnail, .product-gallery__carousel-item, .flickity-page-dots .dot, .flickity-prev-next-button'
      );
      if (thumb) {
        sess.galleryViews = (sess.galleryViews || 0) + 1;
        markStateMutation();
        recordMilestone(sess.galleryViews >= 3 ? 'gal3+' : 'gal1');
      }

      // Image zoom open
      var zoomTrigger = target.closest(
        '.product-gallery__zoom-notice, [data-action="open-product-zoom"], .pswp__button, .product-gallery__image--zoom'
      );
      if (zoomTrigger && !sess.galleryZoomed) {
        sess.galleryZoomed = true;
        markStateMutation();
        recordMilestone('zoom', 'zk_gallery_zoom', true);
      }

      // Links
      var link = target.closest('a[href]');
      if (link) {
        var href = String(link.getAttribute('href') || '');
        if (/\.pdf(\?|#|$)/i.test(href) || link.closest('.zokos-content__list, .zokos-condition__docs, .zokos-quick-links')) {
          if (/\.pdf/i.test(href) || /spec|manual|install|sheet|guide/i.test(link.textContent || '')) {
            sess.pdfClicked = true;
            markStateMutation();
            recordMilestone('pdf', 'zk_pdf_open', true);
          }
        }

        if (/\/pages\/(condition-guide|shipping-returns|shipping-and-returns)/i.test(href)) {
          sess.trustClicked = true;
          markStateMutation();
          recordMilestone('trust', 'zk_trust_click', true);
        }

        if (/\/cart(\?|#|$)/i.test(href)) {
          sess.viewedCart = true;
          markStateMutation();
          recordMilestone('view_cart', null, true);
        }

        if (/\/checkout/i.test(href)) {
          sess.beganCheckout = true;
          markStateMutation();
          syncCartAttributes(true);
          recordMilestone('checkout', 'zk_begin_checkout', true);
        }

        if (link.closest('[data-zokos-similar], .product-recommendations') && /\/products\//i.test(href)) {
          sess.internalPivot = true;
          markStateMutation();
          recordMilestone('pivot', null, true);
        }
      }

      // Condition block click
      if (target.closest('[data-zokos-condition], .zokos-condition')) {
        sess.trustClicked = true;
        recordMilestone('trust');
      }

      // The condition sheet (snippets/zokos-condition-sheet.liquid) opens from these, so the tap changed the page
      if (target.closest('[data-zk-sheet-open], .zokos-condition__list, .zokos-condition__note')) {
        sess.trustClicked = true;
        markStateMutation();
        recordMilestone('trust', 'zk_trust_click', true);
      }

      // A tap outside the sheet's panel lands on the <dialog> itself and closes it (assets/custom.js)
      if (typeof target.matches === 'function' && target.matches('[data-zk-sheet]')) {
        markStateMutation();
      }

      // FAQ click
      if (target.closest('.zokos-content__faq, details summary, .faq__question')) {
        sess.faqOpened = true;
        markStateMutation();
        recordMilestone('faq');
      }

      // 1-tap Model/MPN copy button in snippets/product-meta.liquid
      if (target.closest('[data-zk-copy-mpn]')) {
        sess.skuCopied = true;
        markStateMutation();
        recordMilestone('copy_sku', 'zk_copy_sku', true);
      }

      // 1-tap quick-jump scroll pills under buy buttons
      if (target.closest('[data-zk-scroll-to]')) {
        markStateMutation();
      }

      // Make an Offer button click (its own signal, never add to cart; immediately syncs _zk_* cart attributes
      // so Make an Offer apps that read /cart.js inherit the visitor's Google Ads click/session attribution)
      var offerBtn = findMakeOfferButton(target);
      if (offerBtn) {
        sess.makeOfferClicked = true;
        markStateMutation();
        syncCartAttributes(true);
        recordMilestone('make_offer', 'zk_make_offer', true);
      }

      // Mini-cart toggle
      if (target.closest('[data-action="toggle-mini-cart"], .header__cart-toggle')) {
        sess.viewedCart = true;
        markStateMutation();
        recordMilestone('view_cart', null, true);
      }

      // Add to cart button (in the page or the phone buy bar), never the Make an Offer button that shares its class.
      // The tap only asks for the add: addedToCart waits for the theme's product:added with a successful response
      // (2026-10-25: a failed add counted too, and the cart attribute write here raced the theme's /cart/add.js).
      if (!offerBtn && target.closest('[data-action="add-to-cart"], .product-form__add-button, [data-zk-buybar-atc]')) {
        markStateMutation();
        recordMilestone('atc_tap', null, false);
      }

      // Checkout button or an express checkout button (Shop Pay, Apple Pay, Google Pay, ...), on the page or in the cart
      if (target.closest('[name="checkout"], [data-action="toggle-cart-Checkout"], .cart-recap__checkout, ' + EXPRESS_CHECKOUT_SELECTOR)) {
        beginCheckout();
      }

      writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
    },
    true
  );

  // An express checkout button drawn in an iframe (PayPal) never sends its tap to this document; the window loses
  // focus to it instead, and the element that has focus is the button's host or the iframe itself.
  window.addEventListener('blur', function () {
    var active = document.activeElement;
    if (!active || typeof active.closest !== 'function') return;
    var express =
      active.closest(EXPRESS_CHECKOUT_SELECTOR) ||
      (active.tagName === 'IFRAME' && active.closest('.additional-checkout-buttons, .shopify-payment-button, [data-shopify="payment-button"]'));
    if (express) {
      beginCheckout();
      writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
    }
  });

  // 5. Theme Custom Events (variant:changed, product:added)
  document.addEventListener('variant:changed', function (event) {
    markStateMutation();
    sess.variantChanged = true;
    var prevCond = (window.zokosTrackingProduct && window.zokosTrackingProduct.condition) || null;
    var prevProd = getProductContext();
    if (event && event.detail && event.detail.variant && window.zokosTrackingProduct) {
      var v = event.detail.variant;
      var newCond = v.options && v.options.length ? v.options.join(' / ') : prevProd.condition;
      sess.variantSwitches = sess.variantSwitches || [];
      if (sess.variantSwitches.length < 25) {
        sess.variantSwitches.push({
          ts: nowMs(),
          fromVariantId: prevProd.variantId ? String(prevProd.variantId) : null,
          toVariantId: v.id ? String(v.id) : null,
          fromCondition: prevCond || prevProd.condition || null,
          toCondition: newCond || null,
          fromPriceCents: Number(prevProd.priceCents) || 0,
          toPriceCents: Number(v.price) || 0
        });
      }
      window.zokosTrackingProduct.variantId = v.id;
      window.zokosTrackingProduct.sku = v.sku;
      window.zokosTrackingProduct.priceCents = v.price;
      window.zokosTrackingProduct.compareAtPriceCents = v.compare_at_price;
      if (newCond) {
        window.zokosTrackingProduct.condition = newCond;
      }
    }
    recordMilestone('variant', 'zk_variant_change', true);
  });

  // The theme fires product:added after every add request, with productAdded = response.ok (theme.js BuyButton). An
  // app that fires it without that detail is taken at its word.
  document.addEventListener('product:added', function (event) {
    markStateMutation();
    if (event && event.detail && event.detail.productAdded === false) {
      logCartEvent('atc_failed');
      return;
    }
    sess.addedToCart = true;
    logCartEvent('atc_ok');
    syncCartAttributes(true);
    recordMilestone('atc', 'zk_add_to_cart', true);
  });

  // Cart drawer outcomes (#mini-cart): opened; closed by Back (assets/custom.js marks data-zk-closed-by) or by a tap
  // (the header's X, outside); or still open when the visit ended or went to checkout. elementDwellMs.cart_drawer is
  // the time it stayed open.
  var drawerEl = null;
  var drawerOpenedAt = 0;
  var drawerExitLogged = false;

  function drawerIsOpen() {
    return Boolean(drawerEl && drawerEl.getAttribute('aria-hidden') === 'false');
  }

  function initDrawerObserver() {
    drawerEl = document.querySelector('#mini-cart');
    if (!drawerEl || typeof window.MutationObserver !== 'function') return;
    new window.MutationObserver(function () {
      if (drawerIsOpen()) {
        if (!drawerOpenedAt) {
          drawerOpenedAt = nowMs();
          drawerExitLogged = false;
          logCartEvent('drawer_open');
        }
        return;
      }
      if (!drawerOpenedAt) return;
      sess.elementDwellMs.cart_drawer = (sess.elementDwellMs.cart_drawer || 0) + Math.max(0, nowMs() - drawerOpenedAt);
      drawerOpenedAt = 0;
      var how = drawerEl.getAttribute('data-zk-closed-by') || 'tap';
      drawerEl.removeAttribute('data-zk-closed-by');
      logCartEvent('drawer_close:' + how);
    }).observe(drawerEl, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  // 6. Copy-to-Clipboard on SKU / MPN / Product Title / Specs
  document.addEventListener('copy', function (event) {
    var target = event.target;
    if (
      target &&
      typeof target.closest === 'function' &&
      target.closest('.product-meta, .zokos-content, [data-zokos-rscan-id], .product-block-list__item--info')
    ) {
      sess.skuCopied = true;
      recordMilestone('copy_sku', 'zk_copy_sku', true);
    }
  });

  // 7. JS Runtime Error & Unhandled Promise Rejection Listeners
  window.addEventListener('error', function (event) {
    try {
      var msg = event && event.message ? String(event.message) : '';
      if (!msg || msg === 'Script error.') return;
      sess.errors = sess.errors || [];
      if (sess.errors.length < 20) {
        sess.errors.push({
          id: 'err_' + (sess.errors.length + 1) + '_' + (event.lineno || 0),
          ts: nowMs(),
          msg: msg.slice(0, 200),
          src: event.filename ? String(event.filename).slice(0, 160) : null,
          line: Number(event.lineno) || 0,
          col: Number(event.colno) || 0,
          path: window.location.pathname || '/'
        });
      }
      sess.jsErrors = (sess.jsErrors || 0) + 1;
      writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
    } catch (e) {}
  });

  window.addEventListener('unhandledrejection', function (event) {
    try {
      var reason = event && event.reason;
      var msg = reason && reason.message ? String(reason.message) : String(reason || 'UnhandledRejection');
      if (!msg) return;
      sess.errors = sess.errors || [];
      if (sess.errors.length < 20) {
        sess.errors.push({
          id: 'rej_' + (sess.errors.length + 1),
          ts: nowMs(),
          msg: ('Promise: ' + msg).slice(0, 200),
          src: null,
          line: 0,
          col: 0,
          path: window.location.pathname || '/'
        });
      }
      sess.jsErrors = (sess.jsErrors || 0) + 1;
      writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
    } catch (e) {}
  });

  // 8. Pagehide / Visibilitychange Summary Beacon
  function flushVisitSummary() {
    var t = nowMs();
    sess.lastActiveAt = t;
    if (t - pageLoadedAt < 8000 && !sess.addedToCart && !sess.beganCheckout) {
      sess.quickbackClick = true;
    }
    if (drawerIsOpen() && !drawerExitLogged) {
      drawerExitLogged = true;
      logCartEvent(sess.beganCheckout ? 'drawer_exit:checkout' : 'drawer_exit');
    }
    flushActiveElementDwells();
    writeJsonStorage(window.sessionStorage, SESSION_KEY, sess);
    var scored = computeIntentScore();
    if (scored.isFastBounce10s) {
      syncClarityTags('zk_fast_bounce');
    } else {
      syncClarityTags();
    }
    sendGa4CustomEvent('zk_visit_summary');
    sendFirstPartyBeacon('summary');
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      flushVisitSummary();
    } else {
      var t = nowMs();
      for (var k in activeVisibleStart) {
        if (Object.prototype.hasOwnProperty.call(activeVisibleStart, k) && activeVisibleStart[k] === 0) {
          var el = document.querySelector('[data-zk-observe="' + k + '"]');
          if (el) {
            var r = el.getBoundingClientRect();
            if (r.top < window.innerHeight && r.bottom > 0) {
              activeVisibleStart[k] = t;
            }
          }
        }
      }
    }
  });
  window.addEventListener('pagehide', flushVisitSummary);

  document.addEventListener('visitorConsentCollected', function () {
    if (isAnalyticsAllowed()) {
      if (hasAdAttribution) {
        ensureMainWindowGtag();
        syncClarityTags();
      }
      sendFirstPartyBeacon('consent');
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      applyPrivacyMasks();
      initVisibilityObservers();
      initDrawerObserver();
    });
  } else {
    applyPrivacyMasks();
    initVisibilityObservers();
    initDrawerObserver();
  }
})();
